import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { copy, type Copy } from '../data/copy';
export type Lang = 'es' | 'en';
type LanguageValue = {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: Copy;
};
const STORAGE_KEY = 'lareina-lang';
const LanguageContext = createContext<LanguageValue | null>(null);
function detectLang(): Lang {
  if (typeof window === 'undefined') return 'es';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'es' || stored === 'en') return stored;
  const nav = window.navigator.language?.toLowerCase() ?? 'es';
  return nav.startsWith('en') ? 'en' : 'es';
}
type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLang?: Lang;
};
export function LanguageProvider({
  children,
  defaultLang
}: LanguageProviderProps) {
  const [lang, setLang] = useState<Lang>(() => defaultLang ?? detectLang());
  useEffect(() => {
    if (defaultLang) setLang(defaultLang);
  }, [defaultLang]);
  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable — language still applies for this session */
    }
  }, [lang]);
  const value = useMemo<LanguageValue>(() => ({
    lang,
    setLang,
    t: copy[lang]
  }), [lang]);
  return <LanguageContext.Provider value={value} data-id="element-395" data-mp="32zbhfwxww0ym">{children}</LanguageContext.Provider>;
}
export function useLanguage(): LanguageValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside a LanguageProvider');
  return ctx;
}